'use client';
import React, { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { PropertyCardSkeleton } from '@/components/ui/LoadingSkeleton';
import { createClient } from '@/lib/supabase/client';

type KavlingStatus = 'AVAILABLE' | 'HOLD' | 'BOOKED' | 'SOLD';

interface Kavling {
  id: string;
  name: string;
  project: string;
  cluster: string;
  location: string;
  area_m2: number;
  price: number;
  price_per_m2: number;
  status: KavlingStatus;
  orientation: string;
  elevation: string;
  legalitas: string;
  image_url: string;
  image_alt: string;
  promo: boolean;
  promo_label: string | null;
}

const statuses = ['Semua Status', 'AVAILABLE', 'HOLD', 'BOOKED', 'SOLD'];
const legalitasOptions = ['Semua', 'SHM', 'SHGB'];
const sortOptions = [
  { value: 'terbaru', label: 'Terbaru' },
  { value: 'harga-asc', label: 'Harga Terendah' },
  { value: 'harga-desc', label: 'Harga Tertinggi' },
  { value: 'luas-desc', label: 'Luas Terbesar' },
];

function formatIDR(amount: number): string {
  if (amount >= 1000000000) return `Rp ${(amount / 1000000000).toFixed(1)} M`;
  if (amount >= 1000000) return `Rp ${(amount / 1000000).toFixed(0)} Jt`;
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

export default function PropertyListingContent() {
  const [properties, setProperties] = useState<Kavling[]>([]);
  const [projectOptions, setProjectOptions] = useState<string[]>(['Semua Proyek']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState('Semua Proyek');
  const [selectedStatus, setSelectedStatus] = useState('Semua Status');
  const [selectedLegalitas, setSelectedLegalitas] = useState('Semua');
  const [selectedSort, setSelectedSort] = useState('terbaru');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minArea, setMinArea] = useState('');
  const [promoOnly, setPromoOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [realtimeIndicator, setRealtimeIndicator] = useState(false);
  const itemsPerPage = 9;

  useEffect(() => {
    const supabase = createClient();

    const fetchKavlings = async () => {
      setIsLoading(true);

      // Fetch plots joined with their parent project (only ACTIVE projects)
      const { data, error } = await supabase
        .from('plots')
        .select(`
          id,
          plot_number,
          block,
          area_m2,
          price,
          price_per_m2,
          status,
          orientation,
          elevation,
          legalitas,
          image_url,
          image_alt,
          promo,
          promo_label,
          created_at,
          projects!inner (
            id,
            name,
            location,
            city,
            province,
            status
          )
        `)
        .eq('projects.status', 'ACTIVE')
        .order('created_at', { ascending: true });

      if (!error && data) {
        const mapped: Kavling[] = data.map((plot: any) => {
          const proj = plot.projects;
          const plotName = plot.block
            ? `Kavling ${plot.block}-${plot.plot_number}`
            : `Kavling ${plot.plot_number}`;
          const locationStr = [proj?.city, proj?.province].filter(Boolean).join(', ') || proj?.location || '-';

          // Normalize status: DP_PAID → BOOKED for public display
          let displayStatus: KavlingStatus = plot.status;
          if (plot.status === 'DP_PAID') displayStatus = 'BOOKED';

          return {
            id: plot.id,
            name: plotName,
            project: proj?.name ?? '-',
            cluster: proj?.name ?? '-',
            location: locationStr,
            area_m2: plot.area_m2 ?? 0,
            price: plot.price ?? 0,
            price_per_m2: plot.price_per_m2 ?? 0,
            status: displayStatus,
            orientation: plot.orientation ?? '-',
            elevation: plot.elevation ?? '-',
            legalitas: plot.legalitas ?? '-',
            image_url: plot.image_url ?? '/assets/images/no_image.png',
            image_alt: plot.image_alt ?? plotName,
            promo: plot.promo ?? false,
            promo_label: plot.promo_label ?? null,
          };
        });

        setProperties(mapped);

        // Build dynamic project filter options from fetched data
        const uniqueProjects = Array.from(new Set(mapped.map((k) => k.project))).sort();
        setProjectOptions(['Semua Proyek', ...uniqueProjects]);
      } else {
        setProperties([]);
      }

      setIsLoading(false);
    };

    fetchKavlings();

    // Real-time subscriptions
    const channel = supabase
      .channel('kavlings_realtime_plots')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'plots' }, () => {
        fetchKavlings();
        setRealtimeIndicator(true);
        setTimeout(() => setRealtimeIndicator(false), 3000);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'projects' }, () => {
        fetchKavlings();
        setRealtimeIndicator(true);
        setTimeout(() => setRealtimeIndicator(false), 3000);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'promo_campaigns' }, () => {
        fetchKavlings();
        setRealtimeIndicator(true);
        setTimeout(() => setRealtimeIndicator(false), 3000);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filteredProperties = useMemo(() => {
    let result = properties.filter((p) => {
      if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !p.project.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !p.location.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedProject !== 'Semua Proyek' && p.project !== selectedProject) return false;
      if (selectedStatus !== 'Semua Status' && p.status !== selectedStatus) return false;
      if (selectedLegalitas !== 'Semua' && p.legalitas !== selectedLegalitas) return false;
      if (minPrice && p.price < parseInt(minPrice) * 1000000) return false;
      if (maxPrice && p.price > parseInt(maxPrice) * 1000000) return false;
      if (minArea && p.area_m2 < parseInt(minArea)) return false;
      if (promoOnly && !p.promo) return false;
      return true;
    });

    switch (selectedSort) {
      case 'harga-asc': result.sort((a, b) => a.price - b.price); break;
      case 'harga-desc': result.sort((a, b) => b.price - a.price); break;
      case 'luas-desc': result.sort((a, b) => b.area_m2 - a.area_m2); break;
      default: break;
    }
    return result;
  }, [properties, searchQuery, selectedProject, selectedStatus, selectedLegalitas, minPrice, maxPrice, minArea, promoOnly, selectedSort]);

  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
  const paginatedProperties = filteredProperties.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const activeFiltersCount = [
    selectedProject !== 'Semua Proyek',
    selectedStatus !== 'Semua Status',
    selectedLegalitas !== 'Semua',
    minPrice !== '',
    maxPrice !== '',
    minArea !== '',
    promoOnly,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedProject('Semua Proyek');
    setSelectedStatus('Semua Status');
    setSelectedLegalitas('Semua');
    setMinPrice('');
    setMaxPrice('');
    setMinArea('');
    setPromoOnly(false);
    setSearchQuery('');
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Daftar Kavling</h1>
            <p className="text-sm text-muted-foreground">
              {filteredProperties.length} kavling ditemukan di semua proyek Betaland
            </p>
          </div>
          {/* Real-time indicator */}
          <div className={`flex items-center gap-2 text-xs transition-all ${realtimeIndicator ? 'text-secondary' : 'text-muted-foreground'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeIndicator ? 'bg-secondary animate-ping' : 'bg-secondary/40'}`} />
            {realtimeIndicator ? 'Status diperbarui!' : 'Live'}
          </div>
        </div>
      </div>

      {/* Search + controls bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Cari kavling, proyek, atau lokasi..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors ${
              filterOpen || activeFiltersCount > 0
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-foreground hover:bg-muted'
            }`}
          >
            <Icon name="FunnelIcon" size={16} />
            Filter
            {activeFiltersCount > 0 && (
              <span className="bg-white/30 text-xs px-1.5 py-0.5 rounded-full font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <select
            value={selectedSort}
            onChange={(e) => setSelectedSort(e.target.value)}
            className="px-3 py-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {sortOptions.map((opt) => (
              <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="flex items-center border border-border rounded-lg overflow-hidden bg-card">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="Grid view"
            >
              <Icon name="Squares2X2Icon" size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="List view"
            >
              <Icon name="ListBulletIcon" size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Filter panel */}
      {filterOpen && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Proyek</label>
            <select value={selectedProject} onChange={(e) => { setSelectedProject(e.target.value); setCurrentPage(1); }} className="w-full px-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30">
              {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Status</label>
            <select value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }} className="w-full px-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30">
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Legalitas</label>
            <select value={selectedLegalitas} onChange={(e) => { setSelectedLegalitas(e.target.value); setCurrentPage(1); }} className="w-full px-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30">
              {legalitasOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Harga Min (Jt)</label>
            <input type="number" value={minPrice} onChange={(e) => { setMinPrice(e.target.value); setCurrentPage(1); }} placeholder="150" className="w-full px-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Harga Max (Jt)</label>
            <input type="number" value={maxPrice} onChange={(e) => { setMaxPrice(e.target.value); setCurrentPage(1); }} placeholder="1000" className="w-full px-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="flex flex-col justify-between">
            <label className="flex items-center gap-2 cursor-pointer mt-5">
              <input type="checkbox" checked={promoOnly} onChange={(e) => { setPromoOnly(e.target.checked); setCurrentPage(1); }} className="accent-primary" />
              <span className="text-xs font-medium text-foreground">Promo saja</span>
            </label>
            {activeFiltersCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-red-500 hover:underline text-left mt-1">Reset filter</button>
            )}
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <PropertyCardSkeleton key={`skel-${i}`} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredProperties.length === 0 && (
        <EmptyState
          icon="MagnifyingGlassIcon"
          title="Tidak ada kavling ditemukan"
          description="Coba ubah filter atau kata kunci pencarian Anda"
          action={{ label: 'Reset Filter', onClick: clearFilters }}
        />
      )}

      {/* Grid view */}
      {!isLoading && filteredProperties.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedProperties.map((p) => (
            <Link key={p.id} href={`/kavling/${p.id}`} className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all">
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <AppImage src={p.image_url} alt={p.image_alt} fill className="object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute top-3 left-3 flex gap-2">
                  <StatusBadge status={p.status} size="sm" />
                  {p.promo && p.promo_label && (
                    <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs font-bold rounded-full">{p.promo_label}</span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-muted-foreground mb-0.5">{p.project} · {p.cluster}</p>
                <h3 className="text-base font-bold text-foreground mb-1">{p.name}</h3>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                  <Icon name="MapPinIcon" size={12} />
                  {p.location}
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-primary">{formatIDR(p.price)}</p>
                    <p className="text-xs text-muted-foreground">{p.area_m2} m² · {formatIDR(p.price_per_m2)}/m²</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{p.legalitas}</p>
                    <p className="text-xs text-muted-foreground">{p.orientation}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* List view */}
      {!isLoading && filteredProperties.length > 0 && viewMode === 'list' && (
        <div className="flex flex-col gap-3">
          {paginatedProperties.map((p) => (
            <Link key={p.id} href={`/kavling/${p.id}`} className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all flex gap-4 p-4">
              <div className="relative w-32 h-24 flex-shrink-0 rounded-xl overflow-hidden bg-muted">
                <AppImage src={p.image_url} alt={p.image_alt} fill className="object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="text-xs text-muted-foreground">{p.project}</p>
                    <h3 className="text-sm font-bold text-foreground">{p.name}</h3>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <StatusBadge status={p.status} size="sm" />
                    {p.promo && p.promo_label && (
                      <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs font-bold rounded-full">{p.promo_label}</span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                  <Icon name="MapPinIcon" size={11} />{p.location}
                </p>
                <div className="flex items-center gap-4">
                  <p className="text-base font-bold text-primary">{formatIDR(p.price)}</p>
                  <p className="text-xs text-muted-foreground">{p.area_m2} m²</p>
                  <p className="text-xs text-muted-foreground">{p.legalitas}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors"
          >
            <Icon name="ChevronLeftIcon" size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={`page-${page}`}
              onClick={() => setCurrentPage(page)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${
                currentPage === page ? 'bg-primary text-primary-foreground' : 'border border-border bg-card text-foreground hover:bg-muted'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors"
          >
            <Icon name="ChevronRightIcon" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}